import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PanelLeftOpen, Zap, ArrowUpRight, X, Users, Loader2 } from 'lucide-react';
import { cn } from '@/v2/utils';
import { useUserStore } from '@/store/userStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { orgService, type OrgInvite } from '@/api/orgService';
import { isPaidPlan } from '@/utils/planHelpers';
import { Sidebar } from './Sidebar';
import { Header } from './Header';

// "Running low on credits" notifier — a topbar strip just under the header,
// ported from V1. Shown to paid users whose balance has dropped to ≤10.
// Suppressed in a TEAM workspace (personal credits aren't in play there).
function LowCreditsBanner() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const activeOrgId = useWorkspaceStore((s) => s.activeOrgId);
  const [dismissed, setDismissed] = useState(false);
  const credits = user?.remainingCredit ?? 0;
  const isPro = isPaidPlan(user?.userPlan);
  if (activeOrgId || !isPro || credits > 10 || dismissed) return null;
  return (
    <div className="mx-4 md:mx-6 mt-4 p-3.5 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 flex items-center gap-3 animate-fade-in">
      <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0">
        <Zap className="w-5 h-5 text-amber-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-primary">{t('layout.lowCreditsTitle')}</p>
        <p className="text-xs text-text-tertiary">{t('layout.lowCreditsDescription', { count: credits })}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => navigate('/subscription?context=insufficient_credits')}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold transition-colors"
        >
          <ArrowUpRight className="w-3.5 h-3.5" /> {t('layout.buyCredits')}
        </button>
        <button onClick={() => setDismissed(true)} className="p-1 rounded-md hover:bg-surface-light transition-colors" aria-label={t('layout.dismiss')}>
          <X className="w-4 h-4 text-text-tertiary" />
        </button>
      </div>
    </div>
  );
}

// Pending team-invite notifier — invites addressed to the signed-in user's
// email surface here (besides the email link), accept in one click.
function PendingInviteBanner() {
  const { t } = useTranslation('v2');
  const userId = useUserStore((s) => s.userId);
  const refreshUser = useUserStore((s) => s.refreshUser);
  const setActiveOrg = useWorkspaceStore((s) => s.setActiveOrg);
  const navigate = useNavigate();
  const [invite, setInvite] = useState<OrgInvite | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!userId) return;
    orgService.pendingInvitesForMe(userId)
      .then((invites) => setInvite(invites[0] || null))
      .catch(() => setInvite(null));
  }, [userId]);

  if (!invite || dismissed) return null;

  const accept = async () => {
    if (!userId || accepting) return;
    setAccepting(true);
    try {
      const res = await orgService.acceptInvite(userId, invite.invite_token);
      setActiveOrg(res.org_id);
      await refreshUser();
      setInvite(null);
      navigate('/');
    } catch {
      setDismissed(true);
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="mx-4 md:mx-6 mt-4 p-3.5 rounded-xl bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/25 flex items-center gap-3 animate-fade-in">
      <div className="w-9 h-9 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
        <Users className="w-5 h-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-primary">
          {t('team.inviteBannerTitle', { name: invite.org_name || t('team.teamWorkspace') })}
        </p>
        <p className="text-xs text-text-tertiary">{t('team.inviteBannerDescription')}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={accept}
          disabled={accepting}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary hover:bg-primary/80 text-white text-xs font-semibold transition-colors disabled:opacity-60"
        >
          {accepting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {t('team.acceptInvite')}
        </button>
        <button onClick={() => setDismissed(true)} className="p-1 rounded-md hover:bg-surface-light transition-colors" aria-label={t('layout.dismiss')}>
          <X className="w-4 h-4 text-text-tertiary" />
        </button>
      </div>
    </div>
  );
}

export function Layout() {
  const { t } = useTranslation('v2');
  const location = useLocation();
  // Desktop: sidebar visible by default; Mobile: drawer closed by default
  const [desktopHidden, setDesktopHidden] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Pricing page: collapse the sidebar by default so all three plan cards fit
  // side by side (full-width canvas = maximum card real estate). The user can
  // still reopen it; leaving the page restores the default.
  const isPricingPage = location.pathname.startsWith('/subscription') || location.pathname.startsWith('/upgrade');
  useEffect(() => { setDesktopHidden(isPricingPage); }, [isPricingPage]);

  const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;

  const handleToggle = () => {
    if (isDesktop()) {
      setDesktopHidden((v) => !v);
    } else {
      setMobileOpen((v) => !v);
    }
  };

  return (
    <div className="min-h-screen bg-[#121218]">
      <Sidebar
        desktopHidden={desktopHidden}
        mobileOpen={mobileOpen}
        onClose={handleToggle}
        onNavigate={() => setMobileOpen(false)}
      />

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Floating open button (mobile always when closed, desktop when hidden).
          Ghost style — no box/border — and top-3.5 centers it on the h-16 header. */}
      <button
        onClick={handleToggle}
        className={cn(
          'fixed top-3.5 left-4 z-50 w-9 h-9 rounded-lg',
          'items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-white/5 transition-colors',
          mobileOpen ? 'hidden' : 'flex',
          desktopHidden ? 'lg:flex' : 'lg:hidden'
        )}
        aria-label={t('layout.openSidebar')}
      >
        <PanelLeftOpen className="w-[18px] h-[18px]" strokeWidth={1.75} />
      </button>

      <main
        className={cn(
          'min-h-screen transition-all duration-300',
          desktopHidden ? 'lg:ml-0' : 'lg:ml-64'
        )}
      >
        <Header />
        <PendingInviteBanner />
        <LowCreditsBanner />
        <Outlet />
      </main>
    </div>
  );
}
